import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedSubject, useAuthenticatedSubjectEffect } from '../auth/subject';
import type { ApiKey } from './model';
import { createApiKey } from './repository';
import { apiKeysQueryOptions, cacheApiKey } from './queries';

interface QuickstartKey {
  keys: { data?: ApiKey[]; error: Error | null; isPending: boolean };
  /** Plaintext lives only in this provider's memory and disappears on reload. */
  secret: string | null;
  rememberSecret: (secret: string) => void;
  minting: boolean;
  mint: (name: string) => Promise<void>;
}

const QuickstartKeyContext = createContext<QuickstartKey | null>(null);

export interface QuickstartKeyProviderProps {
  children: ReactNode;
  /** Mint a first key as soon as an account is seen to have none. The welcome
   *  flow turns this off: it mints when the reader reaches the step that hands
   *  the key over, so the secret belongs to a moment they can see. */
  autoMint?: boolean;
  /** False for a Team role that may not see keys, so every page load does not
   *  ask for a list the gateway refuses. */
  keysVisible?: boolean;
}

/** Shares the masked key list and the latest one-time secret across dashboard tabs. */
export const QuickstartKeyProvider = ({
  children,
  autoMint = true,
  keysVisible = true,
}: QuickstartKeyProviderProps) => {
  const subject = useAuthenticatedSubject();
  const runIfCurrent = useAuthenticatedSubjectEffect();
  const queryClient = useQueryClient();
  const keys = useQuery({ ...apiKeysQueryOptions(subject), enabled: keysVisible });
  const [secret, setSecret] = useState<string | null>(null);
  const [minting, setMinting] = useState(false);
  const rememberSecret = useCallback((nextSecret: string) => setSecret(nextSecret), []);

  const mint = useCallback(async (name: string) => {
    setMinting(true);
    try {
      const created = await createApiKey({ name });
      runIfCurrent(() => {
        setSecret(created.secret);
        cacheApiKey(queryClient, subject, created.record);
      });
    } finally {
      runIfCurrent(() => setMinting(false));
    }
  }, [queryClient, runIfCurrent, subject]);

  useEffect(() => {
    if (autoMint && keys.data?.length === 0 && !secret && !minting) void mint('Default key');
  }, [autoMint, keys.data, secret, minting, mint]);

  return (
    <QuickstartKeyContext.Provider value={{ keys, secret, rememberSecret, minting, mint }}>
      {children}
    </QuickstartKeyContext.Provider>
  );
};

export const useQuickstartKey = (): QuickstartKey => {
  const context = useContext(QuickstartKeyContext);
  if (!context) throw new Error('useQuickstartKey requires QuickstartKeyProvider');
  return context;
};

/**
 * The same context for components that render on both sides of the auth line.
 * The provider sits inside `RequireAuth` (routes/dashboard/route.tsx), so its
 * presence is exactly "this reader is signed in" - and a null return is the
 * marketing tree, where Clerk is deliberately not mounted at all
 * (`auth/ClerkScope.tsx`) and no cheaper signed-in signal exists.
 */
export const useOptionalQuickstartKey = (): QuickstartKey | null =>
  useContext(QuickstartKeyContext);
